import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Avatar, Menu,
  MenuItem, BottomNavigation, BottomNavigationAction, Drawer, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Tooltip,
} from '@mui/material';
import {
  MenuBook, Home, RestaurantMenu, Kitchen, LocalDining,
  Settings, Logout, AccountCircle,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AppSwitcher from './AppSwitcher';

const DRAWER_WIDTH = 220;

const NAV_ITEMS = [
  { label: 'Home', path: '/', icon: <Home /> },
  { label: 'Recipes', path: '/recipes', icon: <RestaurantMenu /> },
  { label: 'What Can I Cook?', path: '/cook', icon: <LocalDining /> },
  { label: 'Pantry', path: '/pantry', icon: <Kitchen /> },
  { label: 'Settings', path: '/settings', icon: <Settings /> },
];

const BOTTOM_NAV_ITEMS = [
  { label: 'Home', path: '/', icon: <Home /> },
  { label: 'Recipes', path: '/recipes', icon: <RestaurantMenu /> },
  { label: 'Cook', path: '/cook', icon: <LocalDining /> },
  { label: 'Pantry', path: '/pantry', icon: <Kitchen /> },
];

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const currentBottomTab = BOTTOM_NAV_ITEMS.findIndex(
    (item) => item.path === location.pathname || location.pathname.startsWith(item.path + '/')
  );

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            borderRight: '1px solid rgba(255,255,255,0.06)',
          },
        }}
      >
        {/* Logo — click to switch app */}
        <AppSwitcher>
          <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36, height: 36, borderRadius: '10px',
                background: 'linear-gradient(135deg, #f0873c, #c96820)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <MenuBook sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
              RecipeBook
            </Typography>
          </Box>
        </AppSwitcher>

        <Divider sx={{ opacity: 0.1 }} />

        <List sx={{ px: 1, pt: 1.5, flex: 1 }}>
          {NAV_ITEMS.map((item) => (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                selected={isActive(item.path)}
                sx={{
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: 'rgba(240,135,60,0.15)',
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                  },
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: isActive(item.path) ? 'primary.main' : 'text.secondary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isActive(item.path) ? 600 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ opacity: 0.1 }} />
        <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark', fontSize: '0.85rem' }}>
            {user?.email?.[0]?.toUpperCase() || '?'}
          </Avatar>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user?.email}
          </Typography>
          <Tooltip title="Sign out">
            <IconButton size="small" onClick={handleLogout} sx={{ color: 'text.secondary' }}>
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Mobile AppBar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            display: { md: 'none' },
            bgcolor: 'background.paper',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Toolbar>
            <AppSwitcher>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Box
                  sx={{
                    width: 32, height: 32, borderRadius: '9px',
                    background: 'linear-gradient(135deg, #f0873c, #c96820)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <MenuBook sx={{ fontSize: 18, color: '#fff' }} />
                </Box>
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
                  RecipeBook
                </Typography>
              </Box>
            </AppSwitcher>
            <Box sx={{ flex: 1 }} />
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ color: 'text.secondary' }}>
              <AccountCircle />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { navigate('/settings'); setAnchorEl(null); }}>
                <Settings fontSize="small" sx={{ mr: 1 }} /> Settings
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <Logout fontSize="small" sx={{ mr: 1 }} /> Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box sx={{ flex: 1, overflow: 'auto', pb: { xs: 8, md: 0 } }}>
          <Outlet />
        </Box>

        {/* Mobile Bottom Nav */}
        <BottomNavigation
          value={currentBottomTab === -1 ? false : currentBottomTab}
          onChange={(_, newValue) => navigate(BOTTOM_NAV_ITEMS[newValue].path)}
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            bottom: 0, left: 0, right: 0,
            bgcolor: 'background.paper',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            height: 64,
            '& .Mui-selected': { color: 'primary.main' },
          }}
        >
          {BOTTOM_NAV_ITEMS.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
              sx={{ color: 'text.secondary', '&.Mui-selected': { color: 'primary.main' } }}
            />
          ))}
        </BottomNavigation>
      </Box>
    </Box>
  );
}
